/**
 * Parses the JSON returned by a network request
 *
 * @param response A response from a network request
 * @returns {object} The parsed JSON from the request
 */
function parseJSON(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

/**
 * Checks if message is one of browser default messages for "no internet" error
 *
 * @param message Error message
 * @returns boolean
 */
export function isNetworkErrorMessage(message) {
  return ['Failed to fetch', 'NetworkError when attempting to fetch resource.', 'Network request failed', 'The Internet connection appears to be offline.'].includes(message);
}
export function isOnline() {
  return new Promise(resolve => {
    var _navigator;
    if (((_navigator = navigator) == null ? void 0 : _navigator.onLine) === false) {
      resolve(false);
    } else {
      const ping = fetch('/health', {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      const timeout = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('timeout')), 3000);
      });
      Promise.race([ping, timeout]).then(() => resolve(true)).catch(() => resolve(false));
    }
  });
}
export function logErrorWithOnlineCheck(error) {
  console.error(error); // eslint-disable-line

  if (error.name === 'SyntaxError') {
    throw new Error("Can't parse server response");
  }
  if (error.name === 'TypeError' && isNetworkErrorMessage(error.message)) {
    return isOnline().then(online => {
      if (online) {
        // may be a response timeout, CORS issue or something else
        throw new Error("Can't reach server. Please try again later");
      } else {
        throw new Error('No Internet connection');
      }
    });
  }
  throw error;
}
export function logError(error) {
  console.error(error); // eslint-disable-line

  if (error.name === 'SyntaxError') {
    throw new Error("Can't parse server response");
  }
  if (error.name === 'TypeError' && isNetworkErrorMessage(error.message)) {
    throw new Error("Can't reach server. Please try again later");
  }
  throw error;
}

/**
 * Requests a URL, returning a promise. Small wrapper around fetch
 *
 * @param url fetch url
 * @param options The options we want to pass to "fetch"
 * @param responseInterceptor Response transform function. Parses json by default
 * @param errorInterceptor fetch().catch handler
 * @returns Promise<Response>
 */
export default function request(url, options = {}, responseInterceptor = parseJSON, errorInterceptor = logError) {
  return fetch(url, options).then(response => responseInterceptor(response, options.method)).catch(errorInterceptor);
}